import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import {
  COMPACT_NUMBER_DECIMAL_THRESHOLD,
  COMPACT_NUMBER_K_THRESHOLD,
  COMPACT_NUMBER_M_THRESHOLD,
} from '@/helpers/unit-formatter/unit-formatter.constants';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  toNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';
/**
 * Formats a numeric value with K/M suffixes for compact display.
 * Used by chart axis labels to save horizontal space while maintaining readability.
 */
export function formatCompactNumberValue(value: number): string {
  const absValue = Math.abs(value);

  if (absValue >= COMPACT_NUMBER_M_THRESHOLD) {
    const formatted = (value / COMPACT_NUMBER_M_THRESHOLD).toFixed(1);
    return `${formatted.replace('.0', '')}M`;
  }

  if (absValue >= COMPACT_NUMBER_K_THRESHOLD) {
    const formatted = (value / COMPACT_NUMBER_K_THRESHOLD).toFixed(1);
    return `${formatted.replace('.0', '')}K`;
  }

  if (absValue < COMPACT_NUMBER_DECIMAL_THRESHOLD) {
    return value.toFixed(1);
  }

  return Math.round(value).toString();
}

/**
 * Formatter for compact numeric display (K/M suffixes, axis-friendly precision).
 * Does not display engineering units — only the compact value string.
 */
export const formatCompactNumber = (
  unitValue: UnitValue,
  displayUnits?: string
): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);
  const value = toNumber(transformed.value);

  if (value == null) {
    return unitValueToFormattedUnitLabels({ value: transformed.value, units: undefined });
  }

  const compactValue = formatCompactNumberValue(value);

  return unitValueToFormattedUnitLabels({
    value: compactValue,
    units: undefined,
  });
};
